import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useIntl } from 'react-intl';
import {
  Button,
  TextField,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
} from '@mui/material';
import useAPI from '../../../api';
import {
  CenteredFlexBox,
  ColumnFlexBox,
  FullSizeFlexBox,
} from '../../../Components/FlexBox';
import MultipleChipSelect from '../../../Components/Inputs/MultipleChipSelect';
import FetchAutoComplete from '../../../Components/Inputs/FetchAutoComplete';

const LongChannelFilters = function LongChannelFilters({
  defaultValues = null,
  onConfirm = () => {},
  onClose = () => {},
  branchId,
  dealerRatingOptions = [],
}) {
  const intl = useIntl();
  const API = useAPI();

  const [category, setCategory] = useState(null);
  const [status, setStatus] = useState(null);
  const [categoryOptions, setCategoryOptions] = useState([]);
  const [statusOptions, setStatusOptions] = useState([]);
  const [city, setCity] = useState('');
  const [businessPackage, setBusinessPackage] = useState('');
  const [dealerRating, setDealerRating] = useState('');
  const [hasNationalAgreement, setHasNationalAgreement] = useState('NO');
  const [nationalAgreement, setNationalAgreement] = useState('');

  const handleChangeNationalAgreement = (e) => {
    setNationalAgreement(e.target.value);
  };
  const handleChangeHasNationalAgreement = (e) => {
    setHasNationalAgreement(e.target.value);
  };
  const handleChangeBusinessPackage = (e) => {
    setBusinessPackage(e.target.value);
  };
  const handleDealerRatingChange = (value) => {
    if (!Array.isArray(value) || value.length === 0) setDealerRating(null);
    else {
      setDealerRating(value);
    }
  };
  const handleChangeCity = (e, newValue) => {
    setCity(newValue);
  };

  const getCities = (searchText, onSuccess, onFailure) =>
    branchId
      ? API.searchCitiesByName(
          branchId,
          searchText,
          {
            silent: true,
          },
          (res) => onSuccess(res),
          onFailure
        )
      : Promise.resolve([]);

  const getStatuses = () => {
    API.getDynamicFeaturesById(
      52, //statuses id
      { silent: true },
      (data) => {
        setStatusOptions(data);
      },
      () => {
        setStatusOptions([]);
      }
    );
  };
  const getCategories = () => {
    API.getDynamicFeaturesById(
      53, //categories id
      { silent: true },
      (data) => {
        setCategoryOptions(data);
      },
      () => {
        setCategoryOptions([]);
      }
    );
  };

  useEffect(() => {
    getCategories();
    getStatuses();
  }, []);

  useEffect(() => {
    if (hasNationalAgreement === 'NO') {
      setNationalAgreement('');
    }
  }, [hasNationalAgreement]);

  useEffect(() => {
    if (defaultValues) {
      setCategory(defaultValues?.category ?? null);
      setStatus(defaultValues?.status ?? null);
      setCity(defaultValues?.city ?? '');
      setBusinessPackage(defaultValues?.businessPackage ?? '');
      setDealerRating(defaultValues?.dealerRating ?? '');
      setHasNationalAgreement(
        defaultValues?.hasNationalAgreement
          ? 'YES'
          : defaultValues?.hasNationalAgreement === false
            ? 'NO'
            : 'ALL'
      );
      setNationalAgreement(defaultValues?.nationalAgreement ?? '');
    }
  }, [defaultValues]);

  const handleChangeCategory = (newValues) => {
    if (!Array.isArray(newValues) || newValues.length === 0) setCategory(null);
    else {
      setCategory(newValues);
    }
  };
  const handleChangeStatus = (newValues) => {
    if (!Array.isArray(newValues) || newValues.length === 0) setStatus(null);
    else {
      setStatus(newValues);
    }
  };

  const handleSubmit = () => {
    onConfirm({
      category,
      status,
      city,
      businessPackage,
      dealerRating,
      hasNationalAgreement:
        hasNationalAgreement === 'YES'
          ? true
          : hasNationalAgreement === 'NO'
            ? false
            : hasNationalAgreement === 'ALL'
              ? null
              : null,
      nationalAgreement,
    });
    onClose();
  };

  return (
    <ColumnFlexBox width="600px">
      <FullSizeFlexBox gap="24px">
        <ColumnFlexBox gap="16px" width="50%">
          <MultipleChipSelect
            label={intl.messages.SEGMENT}
            onChange={handleChangeCategory}
            values={category || []}
            options={categoryOptions}
          />

          <FetchAutoComplete
            id="city-automplete"
            label={intl.messages.CITY}
            width="100%"
            selectedValue={city}
            onChangeSelectedValue={handleChangeCity}
            fetchCallback={getCities}
          />
          <FormControl>
            <FormLabel>{intl.messages.HASNATIONALAGREEMENT}</FormLabel>
            <RadioGroup
              value={hasNationalAgreement || 'NO'}
              onChange={handleChangeHasNationalAgreement}
              row
            >
              <FormControlLabel
                value={'YES'}
                control={<Radio />}
                label={intl.messages.YES}
              />
              <FormControlLabel
                value={'NO'}
                control={<Radio />}
                label={intl.messages.NO}
              />
              <FormControlLabel
                value={'ALL'}
                control={<Radio />}
                label={intl.messages.ALL}
              />
            </RadioGroup>
          </FormControl>
          <MultipleChipSelect
            label={intl.messages.DEALERRATING}
            onChange={handleDealerRatingChange}
            values={dealerRating || []}
            options={dealerRatingOptions.map(({ value, displayValue }) => ({
              value,
              label: displayValue,
            }))}
          />
        </ColumnFlexBox>
        <ColumnFlexBox gap="16px" width="50%">
          <MultipleChipSelect
            label={intl.messages.STATUS}
            onChange={handleChangeStatus}
            values={status || []}
            options={statusOptions}
          />
          <TextField
            id="business-package"
            label={intl.messages.BUSINESSPACKAGE}
            value={businessPackage}
            onChange={handleChangeBusinessPackage}
          />
          <TextField
            id="nationalagreement-textfield"
            label={intl.messages.NATIONALAGREEMENT}
            value={nationalAgreement}
            onChange={handleChangeNationalAgreement}
            disabled={hasNationalAgreement !== 'YES'}
          />
        </ColumnFlexBox>
      </FullSizeFlexBox>
      <CenteredFlexBox justifyContent="flex-end" position="sticky" bottom={0}>
        <Button id="confirm-filters-btn" size="large" onClick={handleSubmit}>
          {intl.messages.CONFIRM}
        </Button>
      </CenteredFlexBox>
    </ColumnFlexBox>
  );
};

LongChannelFilters.propTypes = {
  defaultValues: PropTypes.object,
  onConfirm: PropTypes.func,
  onClose: PropTypes.func,
  branchId: PropTypes.string.isRequired,
};

export default LongChannelFilters;
